import React, { useEffect, useRef, useState } from 'react';

import { useAuthContext } from '@/data/hooks/auth';
import authService from '@/data/services/auth.service';

import { AuthErrorDisplay } from './AuthErrorDisplay';

interface AuthErrorHandlerProps {
  children: React.ReactNode;
}

export const AuthErrorHandler: React.FC<AuthErrorHandlerProps> = ({
  children,
}) => {
  const isDevelopment = process.env.VITE_APP_BUILD_ENV === 'dev';
  const { authError, isLoading } = useAuthContext();
  const [showError, setShowError] = useState(false);
  const errorShownRef = useRef(false);

  useEffect(() => {
    // Show error if we have an auth error and we're not loading
    if (!!authError && !isLoading) {
      if (!errorShownRef.current) {
        console.log('🚨 Showing auth error:', authError);
        setShowError(true);
        errorShownRef.current = true;
      }
    } else if (!authError) {
      // Reset when no error
      if (errorShownRef.current) {
        console.log('✅ Clearing auth error display');
      }
      errorShownRef.current = false;
      setShowError(false);
    }
  }, [authError, isLoading]);

  const handleRetry = () => {
    // Clear the error and all auth-related storage
    authService.clearAllAuthData();

    // Reset the error shown state
    errorShownRef.current = false;
    setShowError(false);

    // Reload the page to start fresh with a clean URL
    window.location.replace(window.location.origin);
  };

  if (showError && authError) {
    return (
      <AuthErrorDisplay
        error={authError}
        isDevelopment={isDevelopment}
        onRetry={handleRetry}
      />
    );
  }

  return <>{children}</>;
};
